'use client'

import dynamic from 'next/dynamic'
import { Scissors, Calendar } from 'lucide-react'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { type MowingResult } from '@/lib/mowing-calculator'
import { ProductRecommendations } from '@/components/tools/product-recommendations'
import { ToolDataChartSkeleton } from '@/components/tools/shared'

const MowingDataChart = dynamic(
  () => import('@/components/tools/charts/mowing-data-chart').then((m) => m.MowingDataChart),
  { ssr: false, loading: () => <ToolDataChartSkeleton height={360} /> }
)

interface MowingResultsProps {
  result: MowingResult
}

export function MowingResults({ result }: MowingResultsProps) {
  if (!result || !result.grassType || !result.mowingHeight) {
    return null
  }

  const {
    grassSeason,
    mowingFrequency,
    currentSeason,
    currentRecommendations,
    generalTips,
  } = result

  // Match the user's active season to the labeled column. Cool- and warm-
  // season grasses peak at opposite times of year, so the mapping has to
  // depend on grassSeason — pre-fix, both spring AND fall mapped to "peak"
  // for everyone, which silently mislabeled warm-season summer (actual peak)
  // as "growing" and warm-season fall (winding down) as "peak".
  const seasonKey = currentSeason?.toLowerCase()
  const activeColumn: "growing" | "peak" | "dormant" = (() => {
    if (!seasonKey) return "growing"
    if (seasonKey === "winter") return "dormant"
    if (grassSeason === "Cool") {
      // Cool-season: spring + fall are the peak growing windows; summer is
      // heat-stressed steady-state growth.
      if (seasonKey === "spring" || seasonKey === "fall") return "peak"
      return "growing"
    }
    // Warm-season: summer is peak; spring is wake-up/growing; fall is
    // winding down toward dormancy.
    if (seasonKey === "summer") return "peak"
    return "growing"
  })()

  // Compose a "Right now" callout from the engine's season-specific output.
  // The engine populates currentRecommendations.height + tips per (grassSeason
  // × currentSeason) so that warm-season spring surfaces the one-time scalp
  // tip, cool-season summer surfaces the raised-height guidance, etc. Pre-
  // fix, only `frequency` was rendered — the rest was silently dropped.
  const seasonHeight = currentRecommendations?.height
  const seasonTips = currentRecommendations?.tips ?? []
  const hasSeasonCallout = Boolean(currentSeason && (seasonHeight || seasonTips.length > 0))
  const seasonLabel = currentSeason
    ? currentSeason.charAt(0).toUpperCase() + currentSeason.slice(1)
    : ""

  return (
    <div className="space-y-6">
      {/* "Right now" callout. The engine produces season-specific guidance
          (height + tips) for the user's selected season — e.g. warm-season
          spring → one-time scalp tip, cool-season summer → raised height,
          warm-season winter → "Not applicable". Surfacing it here, above the
          year-round card, gives the user the actionable answer first. */}
      {hasSeasonCallout && (
        <div className="rounded-2xl border border-green-200 bg-green-50/60">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-green-800">
              <Scissors className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              Right now ({seasonLabel})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {seasonHeight && (
              <div className="mb-3 sm:mb-4">
                {seasonHeight === "Not applicable" ? (
                  <p className="text-sm sm:text-base font-medium text-gray-700">
                    No mowing this season. Your grass is dormant.
                  </p>
                ) : (
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-xs sm:text-sm font-medium text-green-700 uppercase tracking-wide">
                      Cut to
                    </span>
                    <span className="text-lg sm:text-xl font-semibold text-gray-900">
                      {seasonHeight}
                    </span>
                  </div>
                )}
              </div>
            )}
            {seasonTips.length > 0 && (
              <ul className="space-y-1.5 sm:space-y-2">
                {seasonTips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 sm:gap-3">
                    <div className="mt-1.5 flex-shrink-0">
                      <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-green-500" />
                    </div>
                    <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </div>
      )}

      {/* Year-round frequency by season. The form's live ruler shows the
          specific number for the user's selected season; this card fills in
          how that frequency shifts across the year. The active season is
          highlighted so users see at a glance which row matches the ruler. */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            Year-round mowing frequency
          </CardTitle>
          {currentRecommendations?.frequency && (
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Right now ({currentSeason || "current season"}): {currentRecommendations.frequency}.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className={`p-3 sm:p-4 rounded-lg border ${activeColumn === "growing" ? "bg-green-50 border-green-300 ring-1 ring-green-300" : "bg-gray-50 border-gray-200"}`}>
              <div className={`text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${activeColumn === "growing" ? "text-green-700" : "text-gray-600"}`}>
                Growing season {activeColumn === "growing" && <span className="ml-1 text-[10px] uppercase tracking-wide">Active</span>}
              </div>
              <div className={`text-base sm:text-lg font-semibold ${activeColumn === "growing" ? "text-green-700" : "text-gray-700"}`}>{mowingFrequency.growingSeason}</div>
              <div className="text-[10px] sm:text-xs text-gray-600 mt-1">Steady growth (typical summer for cool-season, late-spring for warm-season)</div>
            </div>

            <div className={`p-3 sm:p-4 rounded-lg border ${activeColumn === "peak" ? "bg-green-50 border-green-300 ring-1 ring-green-300" : "bg-gray-50 border-gray-200"}`}>
              <div className={`text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${activeColumn === "peak" ? "text-green-700" : "text-gray-600"}`}>
                Peak growth {activeColumn === "peak" && <span className="ml-1 text-[10px] uppercase tracking-wide">Active</span>}
              </div>
              <div className={`text-base sm:text-lg font-semibold ${activeColumn === "peak" ? "text-green-700" : "text-gray-700"}`}>{mowingFrequency.peakGrowth}</div>
              <div className="text-[10px] sm:text-xs text-gray-600 mt-1">Spring or fall surge</div>
            </div>

            <div className={`p-3 sm:p-4 rounded-lg border ${activeColumn === "dormant" ? "bg-green-50 border-green-300 ring-1 ring-green-300" : "bg-gray-50 border-gray-200"}`}>
              <div className={`text-xs sm:text-sm font-medium mb-1 sm:mb-2 ${activeColumn === "dormant" ? "text-green-700" : "text-gray-600"}`}>
                Dormant season {activeColumn === "dormant" && <span className="ml-1 text-[10px] uppercase tracking-wide">Active</span>}
              </div>
              <div className={`text-base sm:text-lg font-semibold ${activeColumn === "dormant" ? "text-green-700" : "text-gray-700"}`}>{mowingFrequency.dormantSeason}</div>
              <div className="text-[10px] sm:text-xs text-gray-600 mt-1">Minimal activity</div>
            </div>
          </div>
        </CardContent>
      </div>

      {/* Best Practices - Single consolidated section */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Scissors className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            Mowing best practices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 sm:gap-x-8 gap-y-2 sm:gap-y-3">
            {generalTips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 sm:gap-3">
                <div className="mt-1.5 flex-shrink-0">
                  <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-green-500" />
                </div>
                <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">{tip}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </div>

      {/* Product Recommendations - Compact mode for calculator context */}
      <ProductRecommendations
        categories={["Lawn Mowers & Maintenance Tools"]}
        title="Mowing equipment"
        trackingContext="mowing-calculator"
        icon={<Scissors className="w-5 h-5 text-green-600" />}
        keywords={['mower', 'lawn mower', 'trimmer', result.grassType].filter(Boolean)}
        compact
      />

      {/* The data behind your recommendation */}
      <MowingDataChart result={result} />

    </div>
  )
}
